import { useEffect, useRef, useState } from 'react'
import { SCENE_COUNT } from './constants'
import { accentFor } from './scenes2d/theme'

import { Scene02Waveform2D } from './scenes2d/Scene02Waveform2D'
import { Scene03Fourier2D } from './scenes2d/Scene03Fourier2D'
import { Scene04Spectrogram2D } from './scenes2d/Scene04Spectrogram2D'
import { Scene05Noise2D } from './scenes2d/Scene05Noise2D'
import { Scene06Speech2D } from './scenes2d/Scene06Speech2D'
import { Scene07Embeddings2D } from './scenes2d/Scene07Embeddings2D'
import { Scene08Clone2D } from './scenes2d/Scene08Clone2D'
import { Scene09TTS2D } from './scenes2d/Scene09TTS2D'
import { Scene10Emotion2D } from './scenes2d/Scene10Emotion2D'
import { Scene11Agents2D } from './scenes2d/Scene11Agents2D'
import { Scene12Universe2D } from './scenes2d/Scene12Universe2D'
import { Scene13Roadmap2D } from './scenes2d/Scene13Roadmap2D'

// Scenes indexed 1–12 (scene 0 stays 3D in the Canvas)
const SCENES_2D = [
  Scene02Waveform2D,
  Scene03Fourier2D,
  Scene04Spectrogram2D,
  Scene05Noise2D,
  Scene06Speech2D,
  Scene07Embeddings2D,
  Scene08Clone2D,
  Scene09TTS2D,
  Scene10Emotion2D,
  Scene11Agents2D,
  Scene12Universe2D,
  Scene13Roadmap2D,
]

const smoothstep = (t) => {
  t = Math.max(0, Math.min(1, t))
  return t * t * (3 - 2 * t)
}

// Mirrors the Slide presence formula from AudioExperience for scenes 1–12.
function presence(slidePos, index) {
  const FADE = 0.25
  return smoothstep((0.5 - Math.abs(slidePos - (index + 0.5))) / FADE)
}

export function AudioScenes2DOverlay() {
  // { index: 1–12, opacity: 0–1 }
  const [active, setActive] = useState({ index: 1, opacity: 0 })
  const rafRef = useRef(null)
  const scrollerRef = useRef(null)

  useEffect(() => {
    const findScroller = () =>
      [...document.querySelectorAll('div')].find((d) => {
        const s = getComputedStyle(d)
        return (
          (s.overflowY === 'auto' || s.overflowY === 'scroll') &&
          d.scrollHeight > d.clientHeight + 1
        )
      })

    let scroller = null

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick)
      if (!scroller) {
        scroller = findScroller()
        scrollerRef.current = scroller
        return
      }
      const maxScroll = scroller.scrollHeight - scroller.clientHeight || 1
      const offset = scroller.scrollTop / maxScroll
      const slidePos = offset * SCENE_COUNT

      // Find which 2D scene (index 1–12) has the highest presence
      let bestIndex = 1
      let bestPresence = 0
      for (let i = 1; i < SCENE_COUNT; i++) {
        const p = presence(slidePos, i)
        if (p > bestPresence) {
          bestPresence = p
          bestIndex = i
        }
      }

      setActive((prev) =>
        prev.index === bestIndex && Math.abs(prev.opacity - bestPresence) < 0.005
          ? prev
          : { index: bestIndex, opacity: bestPresence }
      )
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const Scene = SCENES_2D[active.index - 1]
  const accent = accentFor(active.index)

  if (active.opacity < 0.005) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: active.opacity,
        pointerEvents: 'none',
        // Shift down slightly to match where 3D scenes sit (below the HUD text)
        paddingTop: 220,
      }}
    >
      <div style={{ width: '100%', maxWidth: 560 }}>
        <Scene accent={accent} />
      </div>
    </div>
  )
}
